import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { parseISO, isValid, differenceInDays } from "date-fns"
import { formatInTimeZone } from "date-fns-tz/formatInTimeZone"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date string with consistent styling across the app
 * @param dateString - ISO date string
 * @param language - Current language ('en' or 'ar')
 * @returns Formatted date string with proper capitalization
 */
export function formatAppDate(dateString: string, language: string = 'en', timeZone?: string): string {
  try {
    // Use date-fns parseISO for reliable parsing across browsers
    const date = parseISO(dateString);
    
    // Check if the parsed date is valid
    if (!isValid(date)) {
      console.error('Invalid date string:', dateString);
      return dateString;
    }

    const locale = language === 'ar' ? 'ar-SA-u-ca-gregory' : 'en-US';
    const resolvedTz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const isUtcLike = !resolvedTz || /^(UTC|Etc\/UTC|GMT|Etc\/GMT0|Etc\/GMT|Africa\/Abidjan)$/i.test(resolvedTz);
    const tz = isUtcLike ? 'Asia/Riyadh' : resolvedTz;

    // Time formatted in the target timezone - always with minutes
    const timeFormatted = date.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz,
    });

    // Determine today/tomorrow based on the target timezone
    const todayKey = formatInTimeZone(new Date(), tz, 'yyyy-MM-dd');
    const dateKey = formatInTimeZone(date, tz, 'yyyy-MM-dd');
    const tomorrowKey = formatInTimeZone(new Date(Date.now() + 24 * 60 * 60 * 1000), tz, 'yyyy-MM-dd');

    if (dateKey === todayKey) {
      const todayText = language === 'ar' ? 'اليوم' : 'Today';
      return `${todayText} ${timeFormatted}`;
    } else if (dateKey === tomorrowKey) {
      const tomorrowText = language === 'ar' ? 'غداً' : 'Tomorrow';
      return `${tomorrowText} ${timeFormatted}`;
    } else {
      const diffInDays = differenceInDays(date, new Date());
      if (diffInDays <= 7 && diffInDays > 0) {
        // Within 7 days: show weekday in the target timezone
        const dayName = date.toLocaleDateString(locale, { weekday: 'long', timeZone: tz });
        const capitalizedDay = language === 'en' ?
          dayName.charAt(0).toUpperCase() + dayName.slice(1) : dayName;
        return `${capitalizedDay} ${timeFormatted}`;
      } else {
        // More than 7 days: localized short date + time in the target timezone
        const formatted = date.toLocaleDateString(locale, {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: tz,
        });
        if (language === 'en') {
          return formatted.charAt(0).toUpperCase() + formatted.slice(1);
        }
        return formatted;
      }
    }
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}
